import { BehaviorSubject } from 'rxjs';
import { IPaged } from 'src/shared/models/common/paged';
import { AuthenticatedHttpService, OfflineWrapResult } from './authenticatedHttpService.service';
import { PagedResponse } from 'src/shared/models/common/paged-response';
import { environment } from 'src/environments/environment';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { LocalStorageService } from './local-storage.service';
import * as fp from "lodash/fp";

export abstract class BaseListingProvider<TFilter extends IPaged, TItem> {
	protected get endpoint(): string {
		throw new Error("Not Implemented");
	}
	protected get getErrorMessage(): string {
		throw new Error("Not Implemented");
	}
	protected get storageKeys(): Array<string> { // Need all to perform purge
		throw new Error("Not Implemented");
	}
	protected getStorageKey(filters: TFilter = this._filters.value): string {
		throw new Error("Not Implemented");
	}
	protected transform?: (data: any) => TItem;

	protected readonly initialFilter: TFilter;
	public readonly _filters: BehaviorSubject<TFilter>;

	protected readonly _items = new BehaviorSubject<Array<TItem>>([]);
	public readonly items$ = this._items.asObservable();

	protected readonly _hasMore = new BehaviorSubject(false);
	public readonly hasMore$ = this._hasMore.asObservable();

	protected readonly _error = new BehaviorSubject<string | null>(null);
	public readonly error$ = this._error.asObservable();

	initRequired: boolean | null;

	constructor(
		protected http: AuthenticatedHttpService,
		protected storage: LocalStorageService,
		initialFilter: TFilter,
	) {
		this.initialFilter = initialFilter;
		this._filters = new BehaviorSubject(this.initialFilter);
	}

	async get<TResponse extends PagedResponse<TItem>>(filters?: TFilter) {
		const res = await this.http.wrapIntoOffline<TResponse>({
			fetch: this.http.get,
			params: [`${environment.web.apiUrl}${this.endpoint}?${HttpUtils.toParamsString(filters)}`],
			storeKey: this.getStorageKey(filters),
			renew: !fp.get("page", filters) // keep if first page or all data
		});
		res.result = res.result || (new PagedResponse(new Array<TItem>(), 0) as TResponse);

		if (res.status === OfflineWrapResult.Error)
			throw res.error;

		return res;
	}

	async fetch<TResponse extends PagedResponse<TItem>>(filters = this.initialFilter, append = false) {
		try {
			this.initRequired = null;
			const res = await this.get<TResponse>(filters);

			let items = [...res.result!.items];
			if (append && res.status !== OfflineWrapResult.Offline)
				items = [...(this._items.value || []), ...items];
			if (this.transform)
				items = items.map(item => this.transform!(item));

			this._error.next("");
			this._filters.next(filters);
			this._hasMore.next(res.result!.total > items.length);
			this._items.next(items);

			if (res.status === OfflineWrapResult.Offline)
				this.initRequired = true;

			return res;
		} catch (error) {
			this._error.next(HttpUtils.stringifyError(error, this.getErrorMessage));
			this._items.next([]);
			this._hasMore.next(false);
			this.initRequired = null;

			throw error;
		}
	}

	async loadMore() {
		if (this._hasMore.value) {
			const filter: TFilter = fp.cloneDeep(this._filters.value);
			filter.page++;
			return await this.fetch(filter, true);
		}

		return undefined;
	}

	async purge() {
		this._filters.next(this.initialFilter);
		this._hasMore.next(false);
		this._items.next([]);
		this._error.next(null);
		this.storageKeys.forEach(async (storageKey: string) => {
			await this.storage.remove(storageKey);
		});
	}
}